import { FC, Fragment } from 'react'
import { useIntl } from 'react-intl'
import { useInfiniteQuery, useQueryClient } from 'react-query'

import { TasksAPI } from '@api/tasksAPI'

import { TaskAssignModeEnum, TaskModel, TaskStatusEnum } from '@models/task'

import { ScrollableContainer } from '@components/ScrollableContainer'

import { QUERIES } from '@/constants/queries'
import { useAlert, useToast } from '@/hooks'
import AxiosUtil from '@/utils/axiosUtil'

import { TaskBubble } from '../TaskBubble'

export const DEFAULT_LIMIT_TASKS = 5

type Props = {
  enabled: boolean
  onlyActive: boolean
  taskAssignMode: TaskAssignModeEnum | null
  onTaskUpdated?: () => void
}

const TaskFloatingMenu: FC<Props> = ({ onlyActive, taskAssignMode, onTaskUpdated, enabled }) => {
  const intl = useIntl()
  const alert = useAlert()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, refetch, hasNextPage, fetchNextPage } = useInfiniteQuery(
    [QUERIES.TASK_FLOATING_LIST],
    ({ pageParam = 1 }) =>
      TasksAPI.filter({
        page: pageParam,
        limit: DEFAULT_LIMIT_TASKS,
        task_assign_mode: taskAssignMode,
        statuses: onlyActive ? [TaskStatusEnum.Todo, TaskStatusEnum.Processing] : null,
        priorities: null,
      }),
    {
      enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.data) {
          const { total, page, limit } = lastPage.data

          if (total && page && limit && total / limit > page) {
            return page + 1
          }
        }
      },
    }
  )

  const hasTask = !!data?.pages.at(0)?.data?.nodes.length

  const handleMarkAsDone = async (task: TaskModel) => {
    try {
      const { isConfirmed } = await alert.question({
        text: intl.formatMessage({ id: 'topbar_task.modal.text' }, { name: task.name }),
        confirmButtonText: intl.formatMessage({
          id: 'topbar_task.modal.confirm',
        }),
      })

      if (!isConfirmed) return

      const res = await TasksAPI.updateStatus(task.id, TaskStatusEnum.Done)

      if (res) {
        toast.success(intl.formatMessage({ id: 'topbar_task.toast_success' }))
        queryClient.invalidateQueries(QUERIES.TASK_DETAIL)
        queryClient.invalidateQueries(QUERIES.TASKS_LIST)
        refetch()
        onTaskUpdated?.()
      }
    } catch (err) {
      if (AxiosUtil.isAxiosError(err)) {
        alert.error({ text: err.response?.data.message })
      } else {
        alert.error({ text: String(err) })
      }
      console.error(err)
    }
  }

  if (!data) return null

  return (
    <ScrollableContainer
      className="pb-3 pt-5 px-5 mh-450px overflow-y-auto"
      hasNextPage={hasNextPage}
      next={fetchNextPage}
    >
      {hasTask ? (
        <Fragment>
          {data.pages.map((page) =>
            page.data?.nodes.map((task) => (
              <TaskBubble
                key={task.id}
                task={task}
                onMarkAsDoenClick={handleMarkAsDone}
              />
            ))
          )}
        </Fragment>
      ) : (
        <div className="d-flex flex-column px-9">
          <div className="py-5 text-center text-muted pt-1">No Tasks</div>
        </div>
      )}
    </ScrollableContainer>
  )
}

export { TaskFloatingMenu }
